/**
 * The option lists behind every inquiry field, in one place.
 *
 * These `value`s are the API's vocabulary — changing one silently breaks the
 * stored data and the partner emails, which map the same keys to German on the
 * backend. The labels are what the customer reads.
 *
 * Floors, object types, rooms and availability additionally have display pipes
 * (`inquiryFloor`, `inquiryObjectType`, `inquiryRooms`, `inquiryAvailability`)
 * for read-only rendering; the lists here are what the *editable* controls
 * offer.
 */
import { IconName } from './lucide-icons';

export interface InquiryOption {
  value: string;
  label: string;
  /** Font Awesome classes, for the partner-facing views. */
  icon: string;
  /** What the public inquiry form draws, see `lucide-icons.ts`. */
  lucideIcon: IconName;
  description?: string;
}

export const OBJECT_TYPE_OPTIONS: InquiryOption[] = [
  { value: 'apartment', label: 'Wohnung', icon: 'fas fa-building icon', lucideIcon: 'building-2' },
  { value: 'single_family_house', label: 'Einfamilienhaus', icon: 'fas fa-home icon', lucideIcon: 'house' },
  { value: 'office', label: 'Büro', icon: 'fas fa-store icon', lucideIcon: 'briefcase' },
];

export const ADDITIONAL_SERVICE_OPTIONS: InquiryOption[] = [
  {
    value: 'packing_service',
    label: 'Ein- & Auspackservice',
    icon: 'fas fa-box-open',
    lucideIcon: 'package',
    description: 'Professionelles Verpacken Ihrer Gegenstände',
  },
  {
    value: 'furniture_assembly',
    label: 'Möbelmontage',
    icon: 'fas fa-tools',
    lucideIcon: 'wrench',
    description: 'Ab- und Aufbau Ihrer Möbel',
  },
  {
    value: 'piano_transport',
    label: 'Klaviertransport',
    icon: 'fas fa-music',
    lucideIcon: 'music',
    description: 'Fachgerechter Transport von Klavier oder Flügel',
  },
  {
    value: 'safe_transport',
    label: 'Tresortransport',
    icon: 'fas fa-lock',
    lucideIcon: 'lock',
    description: 'Sicherer Transport schwerer Tresore',
  },
  {
    value: 'storage',
    label: 'Einlagerung',
    icon: 'fas fa-warehouse',
    lucideIcon: 'warehouse',
    description: 'Zwischenlagerung Ihrer Möbel',
  },
];

export const FLOOR_COVERING_OPTIONS: InquiryOption[] = [
  { value: 'laminate', label: 'Laminat', icon: 'fas fa-layer-group', lucideIcon: 'layers' },
  { value: 'parquet', label: 'Parkett', icon: 'fas fa-th-large', lucideIcon: 'grid-2x2' },
  { value: 'tiles', label: 'Fliesen', icon: 'fas fa-th', lucideIcon: 'grid-3x3' },
  { value: 'carpet', label: 'Teppich', icon: 'fas fa-square', lucideIcon: 'square' },
  { value: 'linoleum', label: 'Linoleum', icon: 'fas fa-square', lucideIcon: 'square-dashed' },
];

export const BLINDS_OPTIONS: InquiryOption[] = [
  { value: 'none', label: 'Keine', icon: 'fas fa-ban', lucideIcon: 'ban' },
  { value: 'venetian_blinds', label: 'Lamellenstoren', icon: 'fas fa-th', lucideIcon: 'blinds' },
  { value: 'roller_blinds', label: 'Rollladen', icon: 'fas fa-blinds', lucideIcon: 'panel-top' },
  { value: 'shutters', label: 'Klappläden', icon: 'fas fa-blinds', lucideIcon: 'door-open' },
];

export const ADDITIONAL_ROOM_OPTIONS: InquiryOption[] = [
  { value: 'basement', label: 'Keller', icon: 'fas fa-home', lucideIcon: 'box' },
  { value: 'garage', label: 'Garage', icon: 'fas fa-car', lucideIcon: 'car' },
  { value: 'balcony', label: 'Balkon', icon: 'fas fa-sun', lucideIcon: 'sun' },
  { value: 'attic', label: 'Dachboden', icon: 'fas fa-house-damage', lucideIcon: 'triangle' },
  { value: 'conservatory', label: 'Wintergarten', icon: 'fas fa-leaf', lucideIcon: 'flower' },
];

export const AVAILABILITY_OPTIONS: InquiryOption[] = [
  { value: 'morgens', label: 'Morgens: 8.00-11.30 Uhr', icon: 'fas fa-sun', lucideIcon: 'sunrise' },
  { value: 'mittags', label: 'Mittags: 11.30-13.00 Uhr', icon: 'fas fa-utensils', lucideIcon: 'sun' },
  { value: 'nachmittags', label: 'Nachmittags: 13.00-17.00 Uhr', icon: 'fas fa-cloud-sun', lucideIcon: 'cloud-sun' },
  { value: 'abends', label: 'Abends: ab 17.00 Uhr', icon: 'fas fa-moon', lucideIcon: 'moon' },
  { value: 'durchgehend', label: 'Durchgehend erreichbar', icon: 'fas fa-clock', lucideIcon: 'clock' },
];

export const BATHROOM_COUNT_OPTIONS = [
  { value: '0', label: 'Keine' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4+', label: '4+' },
];

export const FLOOR_OPTIONS: string[] = [
  'ground_floor',
  '1st_floor',
  '2nd_floor',
  '3rd_floor',
  '4th_floor',
  '5th_floor',
  '6th_floor',
  '7th_floor',
  '8th_floor',
  '9th_floor',
  '10th_floor',
  '10th_floor_plus',
];

export const ROOM_OPTIONS: string[] = [
  '1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5', '5.5', '6', '6.5', '7', '7.5', '8', '8+',
];

export const COUNTRY_OPTIONS = [
  { value: 'CH', label: 'Schweiz' },
  { value: 'AL', label: 'Albanien' },
  { value: 'AD', label: 'Andorra' },
  { value: 'BY', label: 'Belarus' },
  { value: 'BE', label: 'Belgien' },
  { value: 'BA', label: 'Bosnien und Herzegowina' },
  { value: 'BG', label: 'Bulgarien' },
  { value: 'DK', label: 'Dänemark' },
  { value: 'DE', label: 'Deutschland' },
  { value: 'EE', label: 'Estland' },
  { value: 'FI', label: 'Finnland' },
  { value: 'FR', label: 'Frankreich' },
  { value: 'GR', label: 'Griechenland' },
  { value: 'IE', label: 'Irland' },
  { value: 'IS', label: 'Island' },
  { value: 'IT', label: 'Italien' },
  { value: 'XK', label: 'Kosovo' },
  { value: 'HR', label: 'Kroatien' },
  { value: 'LV', label: 'Lettland' },
  { value: 'LI', label: 'Liechtenstein' },
  { value: 'LT', label: 'Litauen' },
  { value: 'LU', label: 'Luxemburg' },
  { value: 'MT', label: 'Malta' },
  { value: 'MD', label: 'Moldau' },
  { value: 'MC', label: 'Monaco' },
  { value: 'ME', label: 'Montenegro' },
  { value: 'NL', label: 'Niederlande' },
  { value: 'MK', label: 'Nordmazedonien' },
  { value: 'NO', label: 'Norwegen' },
  { value: 'AT', label: 'Österreich' },
  { value: 'PL', label: 'Polen' },
  { value: 'PT', label: 'Portugal' },
  { value: 'RO', label: 'Rumänien' },
  { value: 'RU', label: 'Russland' },
  { value: 'SM', label: 'San Marino' },
  { value: 'SE', label: 'Schweden' },
  { value: 'RS', label: 'Serbien' },
  { value: 'SK', label: 'Slowakei' },
  { value: 'SI', label: 'Slowenien' },
  { value: 'ES', label: 'Spanien' },
  { value: 'CZ', label: 'Tschechien' },
  { value: 'TR', label: 'Türkei' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'HU', label: 'Ungarn' },
  { value: 'VA', label: 'Vatikanstadt' },
  { value: 'GB', label: 'Vereinigtes Königreich' },
  { value: 'CY', label: 'Zypern' },
];

/** Option lists keyed by value, for rendering a stored key read-only. */
export function optionsByValue(options: InquiryOption[]): Record<string, InquiryOption> {
  return options.reduce<Record<string, InquiryOption>>((all, option) => {
    all[option.value] = option;
    return all;
  }, {});
}

export const OBJECT_TYPES_BY_VALUE = optionsByValue(OBJECT_TYPE_OPTIONS);
export const ADDITIONAL_SERVICES_BY_VALUE = optionsByValue(ADDITIONAL_SERVICE_OPTIONS);
export const FLOOR_COVERINGS_BY_VALUE = optionsByValue(FLOOR_COVERING_OPTIONS);
export const BLINDS_BY_VALUE = optionsByValue(BLINDS_OPTIONS);
export const ADDITIONAL_ROOMS_BY_VALUE = optionsByValue(ADDITIONAL_ROOM_OPTIONS);
export const AVAILABILITY_BY_VALUE = optionsByValue(AVAILABILITY_OPTIONS);

/** Just the icon per value — what the details view binds to `ngClass`. */
export const OBJECT_TYPE_ICONS: Record<string, string> = OBJECT_TYPE_OPTIONS.reduce<Record<string, string>>(
  (all, option) => {
    all[option.value] = option.icon;
    return all;
  },
  {},
);

export const AVAILABILITY_ICONS: Record<string, string> = AVAILABILITY_OPTIONS.reduce<Record<string, string>>(
  (all, option) => {
    all[option.value] = option.icon;
    return all;
  },
  {},
);
